import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, MailCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError } from "@/lib/api";
import Logo from "@/components/Logo";

function Carte({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#F7F4EE] flex items-center justify-center p-5">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-3">
          <Logo size={56} iconSize={30} />
          <h1 className="font-serif font-bold text-2xl">Mastoras</h1>
        </div>
        <div className="panel space-y-4">{children}</div>
        <p className="text-center">
          <Link to="/login" className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1.5"
                data-testid="lien-retour-connexion">
            <ArrowLeft size={14} /> {t("mdp.retour")}
          </Link>
        </p>
      </div>
    </div>
  );
}

export function MotDePasseOublie() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [envoye, setEnvoye] = useState(false);

  async function submit() {
    if (!email.trim()) {
      toast.error(t("mdp.email_requis"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/mot-de-passe-oublie", { email });
      setEnvoye(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Carte>
      <div data-testid="page-mdp-oublie" className="space-y-4">
        <h2 className="font-serif font-semibold text-lg">{t("mdp.titre_oublie")}</h2>
        {envoye ? (
          <div className="text-center space-y-3 py-2" data-testid="confirmation-lien-envoye">
            <MailCheck size={36} className="text-[#3E7C4F] mx-auto" />
            <p className="text-sm text-stone-600">{t("mdp.envoye")}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-stone-500">{t("mdp.intro")}</p>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("mdp.email_label")}</label>
              <input type="email" autoComplete="email" className="input-field" placeholder={t("mdp.email_placeholder")} value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && submit()}
                     data-testid="input-email-reset" />
            </div>
            <button onClick={submit} disabled={loading} className="btn-accent w-full"
                    data-testid="btn-envoyer-lien-reset">
              {loading ? <Loader2 className="animate-spin" size={18} /> : t("mdp.envoyer")}
            </button>
          </>
        )}
      </div>
    </Carte>
  );
}

export function ReinitialiserMotDePasse() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [nouveau, setNouveau] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function submit() {
    if (nouveau.length < 6) {
      toast.error(t("mdp.min6"));
      return;
    }
    if (nouveau !== confirmation) {
      toast.error(t("mdp.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reinitialiser", { token, nouveau });
      toast.success(t("mdp.modifie"));
      navigate("/login");
    } catch (e) {
      toast.error(apiError(e, t("mdp.lien_invalide")));
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <Carte>
        <div className="text-center space-y-3 py-2" data-testid="lien-reset-invalide">
          <ShieldAlert size={36} className="text-[#A32E20] mx-auto" />
          <p className="text-sm text-stone-600">
            {t("mdp.lien_incomplet")}{" "}
            <Link to="/mot-de-passe-oublie" className="text-[#8A5A2B] font-semibold hover:underline">
              {t("mdp.nouveau_lien")}
            </Link>.
          </p>
        </div>
      </Carte>
    );
  }

  return (
    <Carte>
      <div data-testid="page-reinitialiser" className="space-y-4">
        <h2 className="font-serif font-semibold text-lg">{t("mdp.nouveau_titre")}</h2>
        <div>
          <label className="block text-sm font-semibold mb-1.5">{t("mdp.nouveau_label")}</label>
          <input type="password" autoComplete="new-password" className="input-field" placeholder={t("mdp.min6")} value={nouveau}
                 onChange={(e) => setNouveau(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && submit()}
                 data-testid="input-mdp-reset" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5">{t("mdp.confirmer_label")}</label>
          <input type="password" autoComplete="new-password" className="input-field" placeholder={t("mdp.confirmer_placeholder")} value={confirmation}
                 onChange={(e) => setConfirmation(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && submit()}
                 data-testid="input-mdp-reset-confirmation" />
        </div>
        <button onClick={submit} disabled={loading} className="btn-accent w-full"
                data-testid="btn-confirmer-reset">
          {loading ? <Loader2 className="animate-spin" size={18} /> : t("mdp.valider")}
        </button>
      </div>
    </Carte>
  );
}
